import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { apiFetch } from '../../lib/api.js'

const NIVEAUX = [
  'CP', 'CE1', 'CE2', 'CM1', 'CM2',
  '6e', '5e', '4e', '3e', 'CAP',
  '2nde', '1ère', 'Terminale',
  'Supérieur', 'Formation continue', 'Non applicable',
]

const PROFILE_TYPES = [
  { value: 'enseignant-formateur', label: 'Enseignant / Formateur', emoji: '🧑‍🏫' },
  { value: 'eleve-apprenant', label: 'Élève / Apprenant', emoji: '🎒' },
  { value: 'parent', label: 'Parent', emoji: '👪' },
  { value: 'autre', label: 'Autre', emoji: '👤' },
]

function hasConditionalFields(profileType) {
  return ['enseignant-formateur', 'eleve-apprenant', 'parent'].includes(profileType)
}

export default function AdminUserProfile() {
  const { userId } = useParams()
  const [loadingInit, setLoadingInit] = useState(true)
  const [saving, setSaving] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')

  const [userEmail, setUserEmail] = useState('')
  const [prenom, setPrenom] = useState('')
  const [nom, setNom] = useState('')
  const [profileType, setProfileType] = useState('')
  const [etablissement, setEtablissement] = useState('')
  const [codePostal, setCodePostal] = useState('')
  const [niveauxSelected, setNiveauxSelected] = useState([])
  const [niveauSingle, setNiveauSingle] = useState('')
  const [tags, setTags] = useState([])
  const [tagsLoading, setTagsLoading] = useState(false)

  useEffect(() => {
    async function loadProfile() {
      try {
        const data = await apiFetch(`/api/admin/users/${userId}`)
        setUserEmail(data.email || '')
        setPrenom(data.prenom || '')
        setNom(data.nom || '')
        setProfileType(data.profile_type || '')
        setEtablissement(data.etablissement || '')
        setCodePostal(data.code_postal || '')
        if (data.niveaux) {
          if (data.profile_type === 'eleve-apprenant') {
            setNiveauSingle(data.niveaux[0] || '')
          } else {
            setNiveauxSelected(data.niveaux)
          }
        }
        setTags(data.tags || [])
      } catch (e) {
        setError(e.message)
      } finally {
        setLoadingInit(false)
      }
    }
    loadProfile()
  }, [userId])

  function toggleNiveau(niveau) {
    setNiveauxSelected(prev =>
      prev.includes(niveau) ? prev.filter(n => n !== niveau) : [...prev, niveau]
    )
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')
    setSuccess(false)
    setSaving(true)

    try {
      const updates = {
        prenom: prenom.trim(),
        nom: nom.trim(),
        profile_type: profileType || null,
        etablissement: hasConditionalFields(profileType) ? (etablissement.trim() || null) : null,
        code_postal: hasConditionalFields(profileType) ? (codePostal.trim() || null) : null,
        niveaux: null,
      }

      if (profileType === 'enseignant-formateur') {
        updates.niveaux = niveauxSelected.length > 0 ? niveauxSelected : null
      } else if (profileType === 'eleve-apprenant') {
        updates.niveaux = niveauSingle ? [niveauSingle] : null
      }

      await apiFetch(`/api/admin/users/${userId}/profile`, {
        method: 'PATCH',
        body: JSON.stringify(updates),
      })
      setSuccess(true)
    } catch {
      setError('Erreur lors de la sauvegarde. Réessaie.')
    } finally {
      setSaving(false)
    }
  }

  async function handleTagToggle(tag) {
    const action = tags.includes(tag) ? 'remove' : 'add'
    setTagsLoading(true)
    try {
      const data = await apiFetch(`/api/admin/users/${userId}/tags`, {
        method: 'PATCH',
        body: JSON.stringify({ tag, action }),
      })
      setTags(data.tags)
    } catch {
      // silencieux — l'état reste inchangé
    } finally {
      setTagsLoading(false)
    }
  }

  if (loadingInit) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="text-white/40">Chargement…</div>
      </div>
    )
  }

  const displayName = [prenom, nom].filter(Boolean).join(' ') || userEmail

  return (
    <div className="max-w-lg">
      <div className="flex items-center gap-3 mb-6">
        <Link
          to="/admin/users"
          className="text-sm text-white/60 hover:text-white hover:bg-white/10 px-3 py-1.5 rounded-lg transition shrink-0"
        >
          ← Retour
        </Link>
        <h1 className="text-xl font-semibold text-white truncate">
          Profil de {displayName}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="bg-gray-800/50 border border-white/10 rounded-2xl p-5 space-y-4">

        {/* Email (lecture seule) */}
        <div>
          <label className="block text-sm font-medium text-white/60 mb-1">Adresse email</label>
          <input
            type="text"
            value={userEmail}
            readOnly
            className="w-full border border-white/10 bg-white/5 text-white/40 rounded-lg px-3 py-2 text-sm cursor-not-allowed"
          />
        </div>

        {/* Identité */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-white/80 mb-1">Prénom</label>
            <input
              type="text"
              value={prenom}
              onChange={e => setPrenom(e.target.value)}
              className="w-full border border-white/20 bg-white/10 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Prénom"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-white/80 mb-1">NOM</label>
            <input
              type="text"
              value={nom}
              onChange={e => setNom(e.target.value)}
              className="w-full border border-white/20 bg-white/10 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Nom de famille"
            />
          </div>
        </div>

        {/* Type de profil */}
        <div>
          <label className="block text-sm font-medium text-white/80 mb-2">Type de profil</label>
          <div className="grid grid-cols-2 gap-2">
            {PROFILE_TYPES.map(pt => (
              <button
                key={pt.value}
                type="button"
                onClick={() => setProfileType(pt.value)}
                className={`flex items-center gap-2 border-2 rounded-xl px-3 py-2.5 text-sm font-medium transition ${
                  profileType === pt.value
                    ? 'border-primary bg-primary/20 text-white'
                    : 'border-white/20 text-white/70 hover:border-white/40'
                }`}
              >
                <span className="text-lg">{pt.emoji}</span>
                <span>{pt.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Champs conditionnels */}
        {hasConditionalFields(profileType) && (
          <>
            <div>
              <label className="block text-sm font-medium text-white/80 mb-1">
                {profileType === 'parent' ? 'Établissement de ton enfant' : 'Établissement'}
              </label>
              <input
                type="text"
                value={etablissement}
                onChange={e => setEtablissement(e.target.value)}
                className="w-full border border-white/20 bg-white/10 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Nom de l'établissement (optionnel)"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-white/80 mb-1">Code postal</label>
              <input
                type="text"
                value={codePostal}
                onChange={e => setCodePostal(e.target.value.replace(/\D/g, '').slice(0, 5))}
                className="w-full border border-white/20 bg-white/10 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="75000 (optionnel)"
                maxLength={5}
              />
            </div>

            {profileType === 'enseignant-formateur' && (
              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">Niveaux enseignés</label>
                <div className="flex flex-wrap gap-1.5">
                  {NIVEAUX.map(n => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => toggleNiveau(n)}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium border transition ${
                        niveauxSelected.includes(n)
                          ? 'bg-primary text-white border-primary'
                          : 'bg-white/10 text-white/70 border-white/20 hover:border-primary'
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {profileType === 'eleve-apprenant' && (
              <div>
                <label className="block text-sm font-medium text-white/80 mb-1">Niveau scolaire</label>
                <select
                  value={niveauSingle}
                  onChange={e => setNiveauSingle(e.target.value)}
                  className="w-full border border-white/20 bg-gray-700 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">— Choisir —</option>
                  {NIVEAUX.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
            )}
          </>
        )}

        {error && (
          <p className="text-red-300 text-sm bg-red-900/40 border border-red-500/30 rounded-lg px-3 py-2">{error}</p>
        )}
        {success && (
          <p className="text-green-300 text-sm bg-green-900/40 border border-green-500/30 rounded-lg px-3 py-2">
            ✓ Profil mis à jour.
          </p>
        )}

        <button
          type="submit"
          disabled={saving}
          className="btn-primary w-full py-2.5 disabled:opacity-60"
        >
          {saving ? '…' : 'Enregistrer'}
        </button>
      </form>

      {/* Statuts spéciaux */}
      <div className="bg-gray-800/50 border border-white/10 rounded-2xl p-5 mt-4">
        <h3 className="text-xs font-semibold text-white/50 uppercase tracking-widest mb-3">
          Statuts spéciaux
        </h3>
        <button
          type="button"
          disabled={tagsLoading}
          onClick={() => handleTagToggle('early_adopter')}
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border transition disabled:opacity-50 ${
            tags.includes('early_adopter')
              ? 'bg-amber-400/20 text-amber-300 border-amber-400/40'
              : 'bg-white/5 text-white/50 border-white/10 hover:bg-white/10'
          }`}
        >
          ⭐ Early Adopter
        </button>
      </div>
    </div>
  )
}
